import { LinearProgress } from '@mui/material';
import type { GetLightInvoicesResponse } from '@weft/api-definition';
import { dayjs, isEmptyString, isNullOrUndefined } from '@weft/utils';
import { useAccountSuspense } from 'apps/app/src/hooks/requests/use-account';
import { useLightInvoices } from 'apps/app/src/hooks/requests/use-get-light-invoices';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import React, { useEffect, useState } from 'react';
import generatePDF, { Margin } from 'react-to-pdf';
import { LoadingOverlay } from '../../../../components/design/LoadingOverlay';
import { useInvoicesPdfs } from '../../../../hooks/requests/use-invoice-pdf';
import { decodeBase64ToPdfBlob } from '../../../../hooks/use-download-pdf-blob';
import { LightInvoicesAsPdfList } from './LightInvoicesAsPdfList';
import { useLightInvoicesStatementsPdfDownloader } from './UseLightInvoicesStatementDownloader';

type LightInvoicesPdfDownloaderProps = {
  invoicesIds: string[];
  onFinishedDownloading?: () => void;
  isStatement?: boolean;
};

export const LightInvoicesPdfDownloader: React.FC<LightInvoicesPdfDownloaderProps> = ({ invoicesIds, onFinishedDownloading, isStatement }) => {
  const { data, isSuccess } = useLightInvoices(invoicesIds);
  const [downloadedCount, setDownloadedCount] = useState(0);
  const { data: accountData } = useAccountSuspense();
  const { data: invoicesPdfs } = useInvoicesPdfs(invoicesIds);

  const downloadStatementPDFs = useLightInvoicesStatementsPdfDownloader({
    onFinishedDownloading,
    accountName: accountData.account.name,
  });

  useEffect(() => {
    if (isStatement && isSuccess) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      downloadStatementPDFs(data.invoices);
    }
  }, [isSuccess, downloadStatementPDFs, isStatement, data?.invoices]);

  async function handleLoad({ invoices }: GetLightInvoicesResponse) {
    if (downloadedCount > 0 || isStatement) {
      return;
    }

    const getInvoiceQuery = (id: string) => {
      return () => document.getElementById(`invoice-pdf-${id}`);
    };

    const zip = new JSZip();

    for (const invoice of invoices) {
      const { id } = invoice;
      const getInvoice = getInvoiceQuery(id);
      const invoiceWidth = getInvoice()?.clientWidth;
      const invoiceHeight = getInvoice()?.clientHeight;

      if (isNullOrUndefined(invoiceWidth) || isNullOrUndefined(invoiceHeight)) {
        return;
      }

      if (!isStatement) {
        const encodedPdf = invoicesPdfs?.find((pdf) => pdf.invoiceId === id)?.encodedPdf;

        if (isEmptyString(encodedPdf)) {
          return;
        }

        const blob = decodeBase64ToPdfBlob(encodedPdf);
        zip.file(`${invoice.customer.name} ${dayjs(invoice.billingCycle.startTime).format('MMM YYYY')}.pdf`, blob);
      }

      const screenDPI = Math.round(window.devicePixelRatio);
      const pdfDPI = 72;
      const dpiRatio = screenDPI / pdfDPI;
      const adjustedWidth = invoiceWidth * dpiRatio;
      const adjustedHeight = invoiceHeight * dpiRatio;

      if (isStatement) {
        await generatePDF(getInvoice, {
          filename: `${isStatement ? 'statement' : 'invoice'}-${id}.pdf`,
          method: 'save',
          page: {
            format: [adjustedWidth * 10, adjustedHeight * 10],
            margin: Margin.NONE,
          },
        });
      }

      setDownloadedCount((prev) => prev + 1);
    }

    if (!isStatement) {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'invoices.zip');
    }

    onFinishedDownloading?.();
  }

  if (isStatement) {
    return <></>;
  }

  return (
    <>
      <LoadingOverlay
        noSpinner
        show
      >
        <div className='flex flex-col gap-4'>
          <h1>
            {`Downloaded ${downloadedCount} of ${invoicesIds.length} invoices`}
          </h1>
          <LinearProgress
            variant="determinate"
            value={(downloadedCount / invoicesIds.length) * 100}
          />
        </div>
      </LoadingOverlay>
      <div className="min-h-a4 max-w-a4 flex">
        {isSuccess && (
          <LightInvoicesAsPdfList
            invoices={data.invoices}
            companyName={accountData.account.name}
            onLoad={() => handleLoad(data)}
          />
        )}
      </div>
    </>
  );
};